import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { catchError, map, of, switchMap } from 'rxjs';
import { UserResource } from '../../../shared/types/users';
import { ResourceService } from '../../../shared/services/resource.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { ApiError } from '../../../shared/models/error';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    FormGroupDirective,
    NgForm,
    Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { isValidHref } from '../../../shared/directives/link.directive';
import { Icons } from '../../../shared/enums/icons';
import { listSaveEditLabel } from '../../../shared/utils/list-utils';

@Component({
    selector: 'las-resources',
    templateUrl: './resources.component.html',
    styleUrls: ['./resources.component.scss'],
    standalone: false,
})
export class ResourcesComponent implements OnInit {
    @ViewChild('newDialog', { static: true })
    newDialog!: TemplateRef<any>;

    canEdit = true;
    isEditing = false;
    isSaving = false;
    saveAction = 'new';
    resources!: UserResource[];
    error?: string = undefined;
    resourceId: FormControl;
    resourceName: FormControl;
    resourcePath: FormControl;
    resourceForm: FormGroup;
    pathMatcher = new PathErrorStateMatcher();
    locationPlaceholder = 'https://example.com/';
    click: Function;

    constructor(
        private resourceService: ResourceService,
        private dialog: MatDialog,
        builder: FormBuilder,
    ) {
        this.click = this.toggleEdit;
        this.resourceId = new FormControl(0);
        this.resourceName = new FormControl('', [Validators.required]);
        this.resourcePath = new FormControl('', [
            Validators.required,
            pathValidator,
        ]);
        this.resourceForm = builder.group({
            id: this.resourceId,
            name: this.resourceName,
            path: this.resourcePath,
        });
    }

    /**
     * Loads the user's active resources.
     */
    ngOnInit() {
        // We need to subscribe and populate an array; Cdk DnD doesn't like Observable<T>.
        // Additionally, we'd need a copy anyway to use with the drop event.
        this.resourceService
            .getUserResources()
            .pipe(
                map((resources) =>
                    resources.filter((resource) => resource.active),
                ),
                catchError((error) => this.handleError(error)),
            )
            .subscribe((resources) => (this.resources = resources));
    }

    /**
     * Toggles the edit state and sets the icons' click listener.
     */
    toggleEdit() {
        this.isEditing = !this.isEditing;
        this.click = this.isEditing ? this.save : this.toggleEdit;
    }

    /**
     * Saves current state of the resource list and reloads the user's resources.
     */
    save() {
        this.prepForSave();
        this.resourceService
            .saveResources(this.resources)
            .pipe(
                switchMap((_) => this.resourceService.getUserResources(true)),
                catchError((error) => this.handleError(error)),
            )
            .subscribe((resources) => {
                this.resetState(resources);
                this.toggleEdit();
            });
    }

    /**
     * Handles the event resulting from a list item being dragged and dropped.
     * @param event The drag/drop event.
     */
    drop(event: CdkDragDrop<UserResource[]>) {
        moveItemInArray(
            this.resources,
            event.previousIndex,
            event.currentIndex,
        );
    }

    /**
     * Creates a new or updates an existing resource and reloads the user's resources.
     */
    createNewResource() {
        // This should be covered by the save button but savvy users could bypass that.
        if (this.resourceForm.invalid) return;
        this.prepForSave();
        const resource = this.getResourceFromFormFields();
        this.resourceService
            .saveResource(resource, this.saveAction)
            .pipe(
                switchMap((_) => this.resourceService.getUserResources(true)),
                catchError((error) => this.handleError(error)),
            )
            .subscribe((resources) => {
                this.resetState(resources);
                if (this.isEditing) this.toggleEdit();
                this.dialog.closeAll();
            });
    }

    /**
     * Sets the resource to inactive.
     * @param resource The resource to deactivate.
     */
    deactivateResource(resource: UserResource) {
        resource.active = false;
    }

    /**
     * True, if resources exist and there are at least 1.
     */
    hasResources = () => this.resources?.length > 0;

    /**
     * Opens the resource dialog so the user can create a new or edit an existing resource depending on the state of
     * the list.
     * @param resource The newly created resource.
     */
    openNewResourceDialog(resource?: UserResource) {
        if (resource) {
            this.saveAction = 'save';
            this.populateFormFields(resource);
        } else {
            this.saveAction = 'new';
        }

        this.dialog
            .open(this.newDialog, {
                panelClass: 'dialog-responsive',
                width: '30%',
            })
            .afterClosed()
            .subscribe((_) => this.resourceForm.reset());
    }

    private getResourceFromFormFields = (): UserResource => ({
        id: this.resourceId.value ?? 0,
        name: this.resourceName.value,
        path: this.resourcePath.value,
        active: true,
    });

    private populateFormFields(resource: UserResource) {
        this.resourceId.setValue(resource.id);
        this.resourceName.setValue(resource.name);
        this.resourcePath.setValue(resource.path);
        this.resourcePath.markAsDirty();
    }

    private prepForSave() {
        this.clearError();
        this.isSaving = true;
    }

    private resetState(resources: UserResource[]) {
        this.resources = resources;
        this.isSaving = false;
    }

    private clearError = () => (this.error = undefined);

    private handleError(error: ApiError) {
        this.resources ??= [];
        this.resources.forEach((resource) => (resource.active = true));
        this.isSaving = false;
        this.isEditing = false;
        this.error = error.message;
        return of(this.resources);
    }

    protected readonly Icons = Icons;
    protected readonly listSaveEditLabel = listSaveEditLabel;
}

function pathValidator(control: FormControl): { [key: string]: any } | null {
    const value = control.value ?? '';
    return isValidHref(value) ? null : { path: true };
}

class PathErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(
        control: AbstractControl | null,
        form: FormGroupDirective | NgForm | null,
    ): boolean {
        const isSubmitted = form && form.submitted;
        return !!(
            control &&
            control.invalid &&
            (control.dirty || control.touched || isSubmitted)
        );
    }
}
